import type {
  ActionStartResponse,
  ActionStopResponse,
  OfflineSimulationResponse,
  ApiResponse,
  Character,
  CharacterCreateRequest,
  Encounter,
  EnterLocationRequest,
  LoginRequest,
  StartCombatRequest,
  TurnRequest,
  MoveRequest,
  EquipRequest,
  UnequipRequest,
  LoadoutPresetApplyRequest,
  LoadoutPresetApplyResponse,
  LoadoutPresetClearRequest,
  LoadoutPresetClearResponse,
  LoadoutPresetRenameRequest,
  LoadoutPresetRenameResponse,
  LoadoutPresetSaveRequest,
  LoadoutPresetSaveResponse,
  LoadoutPresetsResponse,
  Location,
  Player,
  RegisterRequest,
  TakeGroundLootRequest,
  TakeGroundLootResponse,
  DropItemRequest,
  DropItemResponse,
  BankState,
  BankGoldRequest,
  BankStackableRequest,
  BankInstanceRequest,
  BankInstanceBulkRequest,
  BankDepositAllRequest,
  ContainerOpenRequest,
  ContainerPackAwayRequest,
  ContainerTransferRequest,
  ContainersResponse,
  AcknowledgeRandomEventRequest,
  AcknowledgeRandomEventResponse,
  AcknowledgeDeathEventResponse,
  EncounterTurnResponse,
  UseItemRequest,
  UseItemResponse,
  ProcessingActionResponse,
  EnchantResponse,
  EnchantRequest,
  DisenchantRequest,
  DisenchantResponse,
  MarketHistoryResponse,
  MarketItemListResponse,
  MarketOrderRequest,
  ShopQuoteResponse,
  ShopTradeRequest,
  ShopTradeResponse,
  SocialActionStatusResponse,
  ReadySpellRequest,
  ReadySpellResponse,
  UnreadySpellRequest,
  UnreadySpellResponse,
  MagicCastRequest,
  MagicCastResponse,
  MagicStopRequest,
  MagicStopResponse,
  MagicResumeRequest,
  MagicResumeResponse,
  WsActionKind,
  WsActionPayloadByKind
} from "@idle-rpg/shared";
import { useSessionSocket } from "../composables/useSessionSocket";

// In production (same-origin deployment), use empty string for relative paths.
// In development, fall back to localhost if VITE_API_BASE isn't set.
const API_BASE = import.meta.env.VITE_API_BASE ?? (import.meta.env.PROD ? "" : "http://localhost:8787");
const DEFAULT_API_TIMEOUT_MS = 20000;

type ApiFetchOptions = RequestInit & { token?: string; timeoutMs?: number };

function sleep(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<ApiResponse<T>> {
  const { token, timeoutMs: timeoutOverride, ...fetchOptions } = options;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string> | undefined) || {})
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let res: Response;
  const timeoutMs = Math.max(0, Math.floor(timeoutOverride ?? DEFAULT_API_TIMEOUT_MS));
  const controller = options.signal ? null : new AbortController();
  const signal = options.signal ?? controller?.signal;
  const timeoutId = controller ? window.setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...fetchOptions,
      headers,
      signal
    });
  } catch (error) {
    const isAbort = error instanceof DOMException && error.name === "AbortError";
    const message = isAbort ? "Request timed out" : (error instanceof Error ? error.message : "Network error");
    return { ok: false, error: { message } };
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
  }

  try {
    const json = (await res.json()) as ApiResponse<T>;
    return json;
  } catch (error) {
    return { ok: false, error: { message: `Invalid server response (HTTP ${res.status})` } };
  }
}

async function apiFetchWithRetry<T>(
  path: string,
  options: ApiFetchOptions,
  retry: { attempts?: number; baseDelayMs?: number } = {}
): Promise<ApiResponse<T>> {
  const attempts = Math.max(1, Math.floor(retry.attempts ?? (import.meta.env.PROD ? 1 : 4)));
  const baseDelayMs = Math.max(25, Math.floor(retry.baseDelayMs ?? 250));

  let last: ApiResponse<T> | null = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const res = await apiFetch<T>(path, options);
    last = res;
    if (res.ok) return res;

    const msg = ("error" in res ? res.error?.message : "") || "";
    const transient =
      msg.includes("Invalid server response (HTTP 503)") ||
      msg.includes("Invalid server response (HTTP 502)") ||
      msg.includes("Invalid server response (HTTP 504)") ||
      msg.includes("Network error") ||
      msg.includes("Request timed out");
    if (!transient || attempt >= attempts - 1) return res;

    // Exponential-ish backoff with a small cap to keep UX responsive.
    const delay = Math.min(2000, baseDelayMs * Math.pow(1.6, attempt));
    await sleep(delay);
  }
  return last ?? { ok: false, error: { message: "Unknown error" } };
}

function wsErrorMessage(err: unknown) {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Action failed";
}

function createClientRequestId(value?: string) {
  if (value) return value;
  const cryptoAny = crypto as unknown as { randomUUID?: () => string } | undefined;
  if (cryptoAny?.randomUUID) return cryptoAny.randomUUID();
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

async function wsAction<TData = Record<string, never>, TKind extends WsActionKind = WsActionKind>({
  kind,
  payload,
  clientRequestId,
  timeoutMs
}: {
  kind: TKind;
  payload: WsActionPayloadByKind[TKind];
  clientRequestId?: string;
  timeoutMs?: number;
}): Promise<ApiResponse<TData>> {
  const sessionSocket = useSessionSocket();
  try {
    const ack = await sessionSocket.sendAction({ kind, payload, clientRequestId, timeoutMs });
    return { ok: true, data: (ack.data ?? {}) as TData };
  } catch (err) {
    const message = wsErrorMessage(err);
    const maybeCode = (err as unknown as { code?: unknown }).code;
    const code = typeof maybeCode === "string" && maybeCode.length > 0 ? maybeCode : undefined;
    return { ok: false, error: { message, ...(code ? { code } : {}) } };
  }
}

export function apiRegister(body: RegisterRequest) {
  return apiFetch<{ token: string; player: Player }>("/auth/register", {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export function apiLogin(body: LoginRequest) {
  return apiFetch<{ token: string; player: Player }>("/auth/login", {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export function apiListCharacters(token: string) {
  return apiFetch<{ characters: Character[] }>("/characters", { token });
}

export function apiCreateCharacter(token: string, body: CharacterCreateRequest) {
  return apiFetch<{ character: Character }>("/characters", {
    method: "POST",
    token,
    body: JSON.stringify(body)
  });
}

export function apiTakeGroundLoot(token: string, lootId: string, body: TakeGroundLootRequest) {
  const clientRequestId = createClientRequestId((body as { clientRequestId?: string })?.clientRequestId);
  return wsAction<TakeGroundLootResponse>({
    kind: "ground_loot.take",
    payload: { lootId },
    clientRequestId
  });
}

export function apiUseItem(token: string, characterId: string, body: UseItemRequest) {
  return wsAction<UseItemResponse>({
    kind: "inventory.use_item",
    payload: { itemId: body.itemId },
    clientRequestId: body.clientRequestId
  });
}

export function apiMove(token: string, characterId: string, body: MoveRequest) {
  return wsAction<{ actionId: string; eta: string }>({
    kind: "travel.move",
    payload: { to: body.to },
    clientRequestId: body.clientRequestId
  });
}

export function apiTravelRest(token: string, characterId: string) {
  return wsAction<Record<string, never>>({
    kind: "travel.rest",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiTravelWalk(token: string, characterId: string) {
  return wsAction<Record<string, never>>({
    kind: "travel.walk",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiTravelTurn(token: string, characterId: string) {
  return wsAction<Record<string, never>>({
    kind: "travel.turn",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiStartEncounter(token: string, body: StartCombatRequest & { characterId: string }) {
  return wsAction<{ encounter: Encounter }>({
    kind: "encounter.start",
    payload: { npcStateId: body.npcStateId },
    clientRequestId: body.clientRequestId
  });
}

export function apiSubmitTurn(token: string, body: TurnRequest) {
  return wsAction<EncounterTurnResponse>({
    kind: "encounter.turn",
    payload: {
      encounterId: body.encounterId,
      action: body.action,
      targetId: body.targetId,
      spellId: body.spellId,
      reloadItemId: body.reloadItemId,
      swapContainerInstanceId: body.swapContainerInstanceId,
      quantity: body.quantity
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiGetSpellbook(token: string, characterId: string) {
  void token;
  void characterId;
  const sessionSocket = useSessionSocket();
  return sessionSocket.requestSnapshot("area", { include: ["progress"] })
    .then((snapshot) => ({
      ok: true,
      data: { spellbook: (snapshot.data as { spellbook?: unknown }).spellbook ?? null }
    }))
    .catch((err) => ({ ok: false, error: { message: wsErrorMessage(err) } }));
}

export function apiReadySpell(token: string, body: ReadySpellRequest) {
  return wsAction<ReadySpellResponse>({
    kind: "magic.ready",
    payload: {
      spellId: body.spellId,
      position: body.position
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiUnreadySpell(token: string, body: UnreadySpellRequest) {
  return wsAction<UnreadySpellResponse>({
    kind: "magic.unready",
    payload: { spellId: body.spellId },
    clientRequestId: body.clientRequestId
  });
}

export function apiMagicCast(token: string, body: MagicCastRequest) {
  return wsAction<MagicCastResponse>({
    kind: "magic.cast",
    payload: {
      spellId: body.spellId,
      fromScrollItemId: body.fromScrollItemId ?? null,
      recipeId: body.recipeId ?? null
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiStopMagic(token: string, body: MagicStopRequest) {
  return wsAction<MagicStopResponse>({
    kind: "magic.stop",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiResumeMagic(token: string, body: MagicResumeRequest) {
  return wsAction<MagicResumeResponse>({
    kind: "magic.resume",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiEnterLocation(token: string, characterId: string, body: EnterLocationRequest) {
  return wsAction<{ location: Location }>({
    kind: "location.enter",
    payload: { locationId: body.locationId },
    clientRequestId: createClientRequestId()
  });
}

export function apiExitLocation(token: string, characterId: string) {
  return wsAction<{ locationId: null }>({
    kind: "location.exit",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiSimulateOffline(token: string, characterId: string, clientRequestId: string) {
  return wsAction<OfflineSimulationResponse>({
    kind: "character.simulate_offline",
    payload: {},
    clientRequestId
  });
}

export function apiStartGathering(
  token: string,
  characterId: string,
  nodeId: string,
  clientRequestId: string
) {
  return wsAction<ActionStartResponse>({
    kind: "gather.start",
    payload: { nodeId },
    clientRequestId
  });
}

export function apiStopGathering(token: string, characterId: string, clientRequestId: string) {
  return wsAction<ActionStopResponse>({
    kind: "gather.stop",
    payload: {},
    clientRequestId
  });
}

export function apiStartProcessing(
  token: string,
  characterId: string,
  recipeId: string,
  quantity: number,
  clientRequestId: string
) {
  return wsAction<ProcessingActionResponse>({
    kind: "processing.start",
    payload: {
      recipeId,
      quantity
    },
    clientRequestId
  });
}

export function apiStopProcessing(token: string, characterId: string) {
  return wsAction<{ stopped: boolean; refundedCurrentUnit?: boolean }>({
    kind: "processing.stop",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiPauseProcessing(token: string, characterId: string) {
  return wsAction<{ paused: boolean }>({
    kind: "processing.pause",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiResumeProcessing(token: string, characterId: string) {
  return wsAction<{ resumed: boolean }>({
    kind: "processing.resume",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiDeleteCharacter(token: string, characterId: string) {
  return apiFetch<{ deleted: boolean }>(`/characters/${characterId}`, {
    method: "DELETE",
    token
  });
}

export function apiEquipItem(token: string, characterId: string, body: EquipRequest) {
  return wsAction<{ equipment: Character["equipment"]; inventory: Character["inventory"] }>({
    kind: "equipment.equip",
    payload: {
      itemId: body.itemId,
      instanceId: body.instanceId ?? null,
      slot: body.slot
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiUnequipItem(token: string, characterId: string, body: UnequipRequest) {
  return wsAction<{ equipment: Character["equipment"]; inventory: Character["inventory"] }>({
    kind: "equipment.unequip",
    payload: { slot: body.slot },
    clientRequestId: body.clientRequestId
  });
}

export function apiLoadoutPresets(token: string, characterId: string) {
  return wsAction<LoadoutPresetsResponse>({
    kind: "loadout.list",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiSaveLoadoutPreset(
  token: string,
  characterId: string,
  presetId: string,
  body: LoadoutPresetSaveRequest
) {
  return wsAction<LoadoutPresetSaveResponse>({
    kind: "loadout.save",
    payload: { presetId },
    clientRequestId: body.clientRequestId
  });
}

export function apiApplyLoadoutPreset(
  token: string,
  characterId: string,
  presetId: string,
  body: LoadoutPresetApplyRequest
) {
  return wsAction<LoadoutPresetApplyResponse>({
    kind: "loadout.apply",
    payload: { presetId },
    clientRequestId: body.clientRequestId
  });
}

export function apiRenameLoadoutPreset(
  token: string,
  characterId: string,
  presetId: string,
  body: LoadoutPresetRenameRequest
) {
  return wsAction<LoadoutPresetRenameResponse>({
    kind: "loadout.rename",
    payload: { presetId, name: body.name },
    clientRequestId: body.clientRequestId
  });
}

export function apiClearLoadoutPreset(
  token: string,
  characterId: string,
  presetId: string,
  body: LoadoutPresetClearRequest
) {
  return wsAction<LoadoutPresetClearResponse>({
    kind: "loadout.clear",
    payload: { presetId },
    clientRequestId: body.clientRequestId
  });
}

export function apiDropItem(token: string, characterId: string, body: DropItemRequest) {
  return wsAction<DropItemResponse>({
    kind: "inventory.drop",
    payload: { itemId: body.itemId, qty: body.qty },
    clientRequestId: body.clientRequestId
  });
}

export function apiEnchantItem(token: string, characterId: string, body: EnchantRequest) {
  return wsAction<EnchantResponse>({
    kind: "enchant.apply",
    payload: { instanceId: body.instanceId, enchantmentId: body.enchantmentId },
    clientRequestId: body.clientRequestId
  });
}

export function apiDisenchantItem(token: string, characterId: string, body: DisenchantRequest) {
  return wsAction<DisenchantResponse>({
    kind: "enchant.remove",
    payload: { instanceId: body.instanceId },
    clientRequestId: body.clientRequestId
  });
}

export function apiAcknowledgeDeathEvent(token: string, characterId: string, clientRequestId: string) {
  return wsAction<AcknowledgeDeathEventResponse>({
    kind: "character.death_ack",
    payload: {},
    clientRequestId
  });
}

export function apiAcknowledgeRandomEvent(token: string, characterId: string, clientRequestId: string) {
  return wsAction<AcknowledgeRandomEventResponse>({
    kind: "character.random_event_ack",
    payload: {},
    clientRequestId
  });
}

export function apiLevelUp(token: string, characterId: string) {
  return wsAction<{ newLevel: number; apAwarded: number }>({
    kind: "character.levelup",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiSpendAp(token: string, characterId: string, increments: Record<string, number>) {
  return wsAction<{ attributes: Character["attributes"]; apRemaining: number; max_hp: number; max_mana: number }>({
    kind: "character.spend_ap",
    payload: { increments },
    clientRequestId: createClientRequestId()
  });
}

export function apiShopBuy(token: string, characterId: string, npcId: string, body: ShopTradeRequest) {
  return wsAction<ShopTradeResponse>({
    kind: "shop.buy",
    payload: { npcId, itemId: body.itemId, quantity: body.quantity },
    clientRequestId: createClientRequestId()
  });
}

export function apiShopSell(token: string, characterId: string, npcId: string, body: ShopTradeRequest) {
  return wsAction<ShopTradeResponse>({
    kind: "shop.sell",
    payload: { npcId, itemId: body.itemId, quantity: body.quantity },
    clientRequestId: createClientRequestId()
  });
}

export function apiShopQuote(token: string, characterId: string, npcId: string, itemId: string) {
  return wsAction<ShopQuoteResponse>({
    kind: "shop.quote",
    payload: { npcId, itemId },
    clientRequestId: createClientRequestId()
  });
}

export function apiMarketItems(token: string, characterId: string, query?: string) {
  return wsAction<MarketItemListResponse>({
    kind: "market.items",
    payload: query ? { query } : {},
    clientRequestId: createClientRequestId()
  });
}

export function apiMarketPlaceOrder(token: string, characterId: string, body: MarketOrderRequest) {
  return wsAction<{ created: boolean }>({
    kind: "market.place_order",
    payload: {
      itemId: body.itemId,
      quantity: body.quantity,
      limitPrice: body.limitPrice,
      type: body.type,
      variantKey: body.variantKey ?? null
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiMarketCancelOrder(token: string, characterId: string, orderId: string) {
  return wsAction<{ canceled: boolean }>({
    kind: "market.cancel_order",
    payload: { orderId },
    clientRequestId: createClientRequestId()
  });
}

export function apiMarketHistory(
  token: string,
  characterId: string,
  itemId: string,
  variantKey?: string | null
) {
  return wsAction<MarketHistoryResponse>({
    kind: "market.history",
    payload: { itemId, variantKey },
    clientRequestId: createClientRequestId()
  });
}

export function apiClaimMarketInbox(token: string, characterId: string, id?: string) {
  return wsAction<{ claimed: number }>({
    kind: "market.claim_inbox",
    payload: id ? { id } : {},
    clientRequestId: createClientRequestId()
  });
}

export function apiAdjustSocialOpinion(token: string, characterId: string, npcId: string, delta: number) {
  return wsAction<{ opinion: number; effectiveOpinion: number; bribeCost: number }>({
    kind: "social.adjust",
    payload: { npcId, delta },
    clientRequestId: createClientRequestId()
  });
}

export function apiStartSocialTalk(token: string, characterId: string, npcId: string) {
  return wsAction<SocialActionStatusResponse>({
    kind: "social.talk",
    payload: { npcId },
    clientRequestId: createClientRequestId()
  });
}

export function apiQueueSocialBribe(token: string, characterId: string, npcId: string) {
  return wsAction<SocialActionStatusResponse>({
    kind: "social.bribe",
    payload: { npcId },
    clientRequestId: createClientRequestId()
  });
}

export function apiBankGold(token: string, body: BankGoldRequest) {
  return wsAction<BankState>({
    kind: "bank.gold",
    payload: {
      amount: body.amount,
      direction: body.direction
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiBankStackable(token: string, body: BankStackableRequest) {
  return wsAction<BankState>({
    kind: "bank.stackable",
    payload: {
      itemId: body.itemId,
      quantity: body.quantity,
      direction: body.direction
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiBankInstance(token: string, body: BankInstanceRequest) {
  return wsAction<BankState>({
    kind: "bank.instance",
    payload: {
      instanceId: body.instanceId,
      direction: body.direction
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiBankInstances(token: string, body: BankInstanceBulkRequest) {
  return wsAction<BankState>({
    kind: "bank.instances",
    payload: {
      instanceIds: body.instanceIds,
      direction: body.direction
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiBankDepositAll(token: string, body: BankDepositAllRequest) {
  return wsAction<BankState>({
    kind: "bank.deposit_all",
    payload: {},
    clientRequestId: body.clientRequestId
  });
}

export function apiListContainers(token: string, characterId: string) {
  return wsAction<ContainersResponse>({
    kind: "container.list",
    payload: {},
    clientRequestId: createClientRequestId()
  });
}

export function apiOpenContainer(token: string, body: ContainerOpenRequest) {
  return wsAction<ContainersResponse>({
    kind: "container.open",
    payload: { emptyItemId: body.emptyItemId },
    clientRequestId: body.clientRequestId
  });
}

export function apiPackContainer(token: string, body: ContainerTransferRequest) {
  return wsAction<ContainersResponse>({
    kind: "container.pack",
    payload: {
      containerInstanceId: body.containerInstanceId,
      itemId: body.itemId,
      quantity: body.quantity,
      enchantmentId: body.enchantmentId ?? null
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiUnpackContainer(token: string, body: ContainerTransferRequest) {
  return wsAction<ContainersResponse>({
    kind: "container.unpack",
    payload: {
      containerInstanceId: body.containerInstanceId,
      itemId: body.itemId,
      quantity: body.quantity,
      enchantmentId: body.enchantmentId ?? null
    },
    clientRequestId: body.clientRequestId
  });
}

export function apiPackAwayContainer(token: string, body: ContainerPackAwayRequest) {
  return wsAction<ContainersResponse>({
    kind: "container.pack_away",
    payload: { containerInstanceId: body.containerInstanceId },
    clientRequestId: body.clientRequestId
  });
}
